import { FEATURE } from './browse.js'
import { OPENINGS_DAYS, OPENINGS_LONGEST_NIGHTS } from './publicHome.js'
import { MID_TERM_MINIMUM_NIGHTS } from '../availability/search.js'
import { COMPANY_TZ } from '../leads/ui.js'

/**
 * WHAT THE SEARCH PAGE IS SHOWING, AS ONE PLAIN OBJECT — and the address that carries it.
 *
 * Decision 6 (project doc 105): a search can be shared, and the link opens the same search. So
 * everything the page filters by lives here and is written into the address; nothing about what she
 * sees is the page's private state. Pure functions only — the page draws, this decides.
 */

export const BLANK_SEARCH = Object.freeze({
  where: '', checkIn: '', checkOut: '',
  beds: 0, baths: 0, types: [], features: [],
  min: null, max: null, availableNow: false, sort: 'REC',
  /* Staff only, and never written into a shared link: a renter's page has no such homes. */
  notReady: false,
})

export const SORTS = Object.freeze([
  { id: 'REC', label: 'Soonest available' },
  { id: 'LO', label: 'Price: low to high' },
  { id: 'HI', label: 'Price: high to low' },
  { id: 'BEDS', label: 'Most bedrooms' },
])

/**
 * "AVAILABLE NOW" MEANS SHE COULD MOVE IN WITHIN THIS MANY DAYS. Two weeks is the window a renter
 * who searches "now" is usually working to — a relocation, a lease that just ended. It is a choice,
 * and it is here, named, so it can be changed in one place.
 */
export const AVAILABLE_NOW_DAYS = 14

const DAY_MS = 86_400_000
const daysFrom = (from, to) => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS)

const list = (v) => String(v ?? '').split(',').map((s) => s.trim()).filter(Boolean)
const whole = (v) => { const n = Number(v); return Number.isInteger(n) && n > 0 ? n : 0 }
const money = (v) => { const n = Number(v); return v !== '' && v != null && Number.isFinite(n) && n >= 0 ? Math.round(n) : null }

/** The search an address describes. Anything malformed is left at its blank value, never guessed. */
export function searchFromQuery(query) {
  const q = query instanceof URLSearchParams ? query : new URLSearchParams(String(query ?? '').replace(/^\?/, ''))
  const sort = SORTS.some((s) => s.id === q.get('sort')) ? q.get('sort') : 'REC'
  /* The same two names a home's own link uses, so a search's dates and a home's dates read alike. */
  const stay = stayFromQuery(q.toString())
  return {
    ...BLANK_SEARCH,
    where: (q.get('where') ?? '').slice(0, 60),
    checkIn: stay?.checkIn ?? '',
    checkOut: stay?.checkOut ?? '',
    beds: whole(q.get('beds')),
    baths: whole(q.get('baths')),
    types: list(q.get('type')).slice(0, 10),
    features: list(q.get('f')).filter((f) => FEATURE[f]),
    min: money(q.get('min')),
    max: money(q.get('max')),
    availableNow: q.get('now') === '1',
    sort,
  }
}

/** The address of a search: only what differs from blank, in a fixed order, so equal searches share a link. */
export function queryFromSearch(s) {
  const q = new URLSearchParams()
  if (s.where) q.set('where', s.where)
  if (s.checkIn && s.checkOut) { q.set('checkIn', s.checkIn); q.set('checkOut', s.checkOut) }
  if (s.beds) q.set('beds', String(s.beds))
  if (s.baths) q.set('baths', String(s.baths))
  if (s.types?.length) q.set('type', [...s.types].sort().join(','))
  if (s.features?.length) q.set('f', [...s.features].sort().join(','))
  if (s.min != null) q.set('min', String(s.min))
  if (s.max != null) q.set('max', String(s.max))
  if (s.availableNow) q.set('now', '1')
  if (s.sort && s.sort !== 'REC') q.set('sort', s.sort)
  const out = q.toString()
  return out ? `?${out}` : ''
}

/** Has she chosen a stay — the thing every price on the page waits for. */
export const hasStay = (s) => Boolean(s.checkIn && s.checkOut)

/**
 * The homes the search shows, in the order it shows them. `prices` is the server's answer for her
 * stay ({ [key]: { averageMonthlyCents, free } }) or null before she has chosen one.
 *
 * A price filter with no stay chosen filters nothing — there are no prices to compare — and a home
 * with no price for her stay is kept rather than dropped: "we could not price it" is not "too dear".
 * A home taken for her stay is dropped: it is not something she can book.
 */
export function visibleHomes(homes = [], s, { prices = null, today, staff = false } = {}) {
  const priceOf = (h) => prices?.[h.key]?.averageMonthlyCents ?? null
  const out = homes.filter((h) => {
    if (staff && s.notReady && h.preview?.listable !== false) return false
    if (s.where && h.city !== s.where) return false
    if (s.beds && !(Number(h.bedrooms) >= s.beds)) return false
    if (s.baths && !(Number(h.bathrooms) >= s.baths)) return false
    if (s.types?.length && !s.types.includes(h.propertyType)) return false
    if (s.features?.length && !s.features.every((f) => (h.features ?? []).includes(f))) return false
    if (s.availableNow && !(h.availableFrom && today && daysFrom(today, h.availableFrom) <= AVAILABLE_NOW_DAYS)) return false
    /*
      TAKEN FOR HER DATES IS NOT A RESULT (the Product Owner, 2026-10-07 11:27: "只显示available才是对的…
      根本连搜都搜不到啊"). Every booking site works this way: a search for dates shows the homes she can
      have for them.
    */
    if (prices?.[h.key]?.free === false) return false
    if (prices) {
      const p = priceOf(h)
      if (p != null && s.min != null && p < s.min * 100) return false
      if (p != null && s.max != null && p > s.max * 100) return false
    }
    return true
  })
  const byPrice = (dir) => (a, b) => {
    const pa = priceOf(a); const pb = priceOf(b)
    if (pa == null && pb == null) return 0
    if (pa == null) return 1
    if (pb == null) return -1
    return dir * (pa - pb)
  }
  /*
    RECOMMENDED IS "SOONEST SHE CAN MOVE IN" (the Product Owner, 2026-10-07 11:38). A home with no
    opening in the next six months goes last.
  */
  const soonest = (a, b) => (a.availableFrom ?? '9999') .localeCompare(b.availableFrom ?? '9999')
  const order = s.sort === 'LO' ? byPrice(1) : s.sort === 'HI' ? byPrice(-1)
    : s.sort === 'BEDS' ? (a, b) => (Number(b.bedrooms) || 0) - (Number(a.bedrooms) || 0) : soonest
  return out.map((h, i) => ({ h, i }))
    .sort((a, b) => order(a.h, b.h) || a.i - b.i)
    .map((x) => x.h)
}

/** How many of the Filters dialog's own settings are on — the number on its chip. */
export function filterCount(s) {
  return (s.min != null ? 1 : 0) + (s.max != null ? 1 : 0) + (s.beds ? 1 : 0) + (s.baths ? 1 : 0)
    + (s.types?.length ?? 0) + (s.features?.length ?? 0) + (s.availableNow ? 1 : 0) + (s.where ? 1 : 0)
}

/*
  THE SEARCH PAGE'S CALENDAR IS A HOME PAGE'S CALENDAR (`PickDates`, unchanged; the Product Owner,
  2026-10-07 11:58: "不要再重新开发…做的跟之前的一模一样"). On a home's page it is told which days that
  home can be had; here no one home is chosen yet, so it is told the page's own limits — the same ones
  a home's page offers within: a move-in from today for as many days as that page offers, and a stay
  of a minimum stay to a year. Which homes are free for the days she picks is then the search's answer.
*/
const DAY = 86_400_000
const plusDays = (iso, n) => new Date(Date.parse(`${iso}T00:00:00Z`) + n * DAY).toISOString().slice(0, 10)
const daysFromTo = (from, to) => { const out = []; for (let d = from; d <= to; d = plusDays(d, 1)) out.push(d); return out }

/** The move-in days, in the shape `PickDates` takes as `moveIn`. */
export function searchMoveIns(today) {
  const through = plusDays(today, OPENINGS_DAYS - 1)
  return { from: today, through, open: daysFromTo(today, through), needsOwnerOk: [] }
}

/** The move-out days for one move-in, in the shape `PickDates` takes as `leaving`. */
export function searchMoveOuts(checkIn) {
  if (!checkIn) return null
  return { for: checkIn, open: daysFromTo(plusDays(checkIn, MID_TERM_MINIMUM_NIGHTS), plusDays(checkIn, OPENINGS_LONGEST_NIGHTS)), needsOwnerOk: [] }
}

/** The office's day for a moment — the day the page and the server both call "today". */
export const companyDayOf = (iso) => new Intl.DateTimeFormat('en-CA', { timeZone: COMPANY_TZ, year: 'numeric', month: '2-digit', day: '2-digit' })
  .format(new Date(iso))

/*
  A HOME'S LINK CARRIES HER DATES (the Product Owner, 2026-10-07: "带过去吧"). Opening a card, or sharing
  one after choosing a stay, sends /stay/<home>?checkIn=…&checkOut=…, and the home's page opens on them.
*/
const ISO = /^\d{4}-\d{2}-\d{2}$/

export function homeLinkPath(path, stay = null) {
  if (!stay || !ISO.test(stay.checkIn ?? '') || !ISO.test(stay.checkOut ?? '')) return path
  return `${path}?checkIn=${stay.checkIn}&checkOut=${stay.checkOut}`
}

/** The stay a home's link carries, or null. A stay that has begun, or runs backwards, is no stay. */
export function stayFromQuery(query, today) {
  const q = new URLSearchParams(String(query ?? '').replace(/^\?/, ''))
  const checkIn = q.get('checkIn'); const checkOut = q.get('checkOut')
  if (!ISO.test(checkIn ?? '') || !ISO.test(checkOut ?? '')) return null
  if (Number.isNaN(Date.parse(`${checkIn}T00:00:00Z`)) || Number.isNaN(Date.parse(`${checkOut}T00:00:00Z`))) return null
  if (checkOut <= checkIn || (today && checkIn < today)) return null
  return { checkIn, checkOut }
}
